import { useCallback, useState } from 'react';
import {
  Content,
  Inline,
  InlineItem,
  Placeholder,
  Secondary,
  Stack,
  Text,
  UtilityButton,
} from '@kinesis/bungle';
import type {
  ScopeRuleAxis,
  InventoryScopeRule,
  ScopeRuleDraft,
} from '@/types';
import { createSkeleton } from '@/data/scope-rules';
import { ScopeRuleCard } from './scope-rule-card';
import { AddRuleModal } from './add-rule-modal';

// The scenario scope-rule editor — the community analogue of suit's
// `InventoryScopeControl`, shifted to village's flat five-axis grain. Edits an
// array of draft rules (saved rules carry an id; new ones don't) and reports
// the whole next array via `onChange`; the page holds the local state and
// reconciles it against the saved set on Save (matching suit's batched
// edit-then-save model). Presentational — no queries here.
type InventoryScopeControlProps = {
  rules: ScopeRuleDraft[];
  // Disabled (viewer role): hides Add rule + the cards' delete/edit affordances.
  disabled?: boolean;
  onChange?: (next: ScopeRuleDraft[]) => void;
};

const InventoryScopeControl = (props: InventoryScopeControlProps) => {
  const { rules, disabled, onChange } = props;
  const [isAddRuleOpen, setAddRuleOpen] = useState(false);

  // Keyed by array index — newly-added rules have no id until Save declares
  // them, so position is the authoritative local identity (as in suit).
  const handleRuleChange = useCallback(
    (index: number) => (specifier: InventoryScopeRule) => {
      if (!onChange) return;
      onChange(
        rules.map((rule, i) => (i === index ? { ...rule, specifier } : rule)),
      );
    },
    [rules, onChange],
  );

  const handleRuleRemove = useCallback(
    (index: number) => () => {
      if (!onChange) return;
      onChange(rules.filter((_, i) => i !== index));
    },
    [rules, onChange],
  );

  const handleAddRule = useCallback(
    (axis: ScopeRuleAxis) => {
      setAddRuleOpen(false);
      if (!onChange) return;
      onChange([...rules, { specifier: createSkeleton(axis) }]);
    },
    [rules, onChange],
  );

  const noRules = rules.length === 0;

  return (
    <Stack space='medium'>
      <Content border padding='small' paddingMode='equal' height={60}>
        <Text>
          Include all activity in the inventory except where trimmed by the
          rules below. Each rule removes its matched emissions from this
          scenario&rsquo;s totals.
        </Text>
      </Content>
      {noRules && (
        <Content
          alignX='center'
          textAlign='center'
          padding='medium'
          paddingMode='equal'
        >
          <Placeholder>
            No scope rules yet. This scenario covers the entire inventory.
          </Placeholder>
        </Content>
      )}
      {rules.map((rule, index) => (
        <ScopeRuleCard
          key={rule.id != null ? `id-${rule.id}` : `new-${index}`}
          rule={rule}
          disabled={disabled}
          onChange={handleRuleChange(index)}
          onRemove={handleRuleRemove(index)}
        />
      ))}
      <Content alignX='right'>
        <Inline>
          <InlineItem sizing='fill-container'>
            <Secondary>
              {rules.length === 1
                ? '1 scope rule'
                : `${rules.length} scope rules`}
            </Secondary>
          </InlineItem>
          <InlineItem>
            <UtilityButton
              icon='plus'
              magnitude='small'
              onClick={() => setAddRuleOpen(true)}
              disabled={disabled}
            >
              <Text>Add rule</Text>
            </UtilityButton>
          </InlineItem>
        </Inline>
      </Content>
      {isAddRuleOpen && (
        <AddRuleModal
          onClose={() => setAddRuleOpen(false)}
          onSelect={handleAddRule}
        />
      )}
    </Stack>
  );
};

export { InventoryScopeControl };
export type { InventoryScopeControlProps };
